import { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Platform, Text, View } from 'react-native';

const TURNSTILE_SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

type TurnstileRenderOptions = {
  sitekey: string;
  theme?: 'auto' | 'light' | 'dark';
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
};

type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (window.turnstile) {
    return Promise.resolve();
  }

  const existingScript = document.querySelector(
    `script[src="${TURNSTILE_SCRIPT_URL}"]`,
  ) as HTMLScriptElement | null;

  if (existingScript) {
    return new Promise((resolve, reject) => {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Failed to load Cloudflare Turnstile.')),
        { once: true },
      );
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Cloudflare Turnstile.'));
    document.head.appendChild(script);
  });
}

type TurnstileCaptchaProps = {
  siteKey: string;
  resetKey?: number;
  onTokenChange: (token: string | null) => void;
  onErrorChange?: (message: string | null) => void;
};

export function TurnstileCaptcha({
  siteKey,
  resetKey = 0,
  onTokenChange,
  onErrorChange,
}: TurnstileCaptchaProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || !siteKey) {
      setIsLoading(false);
      return;
    }

    let isCancelled = false;

    const renderWidget = async () => {
      try {
        await loadTurnstileScript();

        if (isCancelled || !containerRef.current || !window.turnstile) {
          return;
        }

        if (widgetIdRef.current) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }

        onTokenChange(null);
        onErrorChange?.(null);
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme: 'dark',
          callback: (token) => {
            onTokenChange(token);
            onErrorChange?.(null);
          },
          'expired-callback': () => {
            onTokenChange(null);
            onErrorChange?.('Security check expired. Please verify again.');
          },
          'error-callback': () => {
            onTokenChange(null);
            onErrorChange?.('Security check failed to load. Refresh and try again.');
          },
        });
        setIsLoading(false);
      } catch (error) {
        if (isCancelled) {
          return;
        }

        onTokenChange(null);
        onErrorChange?.(
          error instanceof Error ? error.message : 'Failed to load Cloudflare Turnstile.',
        );
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    void renderWidget();

    return () => {
      isCancelled = true;

      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [onErrorChange, onTokenChange, siteKey]);

  useEffect(() => {
    if (
      Platform.OS !== 'web' ||
      typeof window === 'undefined' ||
      !widgetIdRef.current ||
      !window.turnstile
    ) {
      return;
    }

    window.turnstile.reset(widgetIdRef.current);
    onTokenChange(null);
    onErrorChange?.(null);
  }, [onErrorChange, onTokenChange, resetKey]);

  if (Platform.OS !== 'web' || !siteKey) {
    return null;
  }

  return (
    <View className="mb-4 w-full rounded-xl border border-white/10 bg-gray-900/60 px-4 py-4">
      <View
        className="items-center justify-center"
        style={isLoading ? { minHeight: 72 } : undefined}
      >
        {isLoading ? (
          <>
            <ActivityIndicator color="#d1d5db" />
            <Text className="mt-3 text-sm text-gray-400">Loading security check...</Text>
          </>
        ) : null}
        <div ref={containerRef} />
      </View>
    </View>
  );
}
